import React, { useEffect, useRef, useState } from "react";
import {
  useController,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form";
import { ChevronDown, Check, X, Search } from "lucide-react";
import FormFieldShell from "./FormFieldShell";
import { cn } from "@/lib/utils";
import type { OptionItem } from "./SelectDropdown";

export interface MultiSelectDropdownProps<
  TFieldValues extends FieldValues = any,
> {
  label?: string;
  placeholder?: string;
  options: OptionItem[];
  value?: (string | number)[];
  onChange?: (value: (string | number)[]) => void;
  onSearch?: (searchQuery: string) => void;
  className?: string;
  dropDownContainerClass?: string;
  control?: Control<TFieldValues>;
  name?: Path<TFieldValues>;
  required?: boolean;
  disabled?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
  isSearchable?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  onMenuScrollToBottom?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

interface MultiSelectDropdownBaseProps {
  options: OptionItem[];
  value?: (string | number)[];
  onChange?: (value: (string | number)[]) => void;
  onSearch?: (searchQuery: string) => void;
  placeholder?: string;
  className?: string;
  dropDownContainerClass?: string;
  shouldShowError?: boolean;
  disabled?: boolean;
  isSearchable?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  id?: string;
  onMenuScrollToBottom?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

const MultiSelectDropdownBase: React.FC<MultiSelectDropdownBaseProps> = ({
  options,
  value = [],
  onChange,
  onSearch,
  placeholder = "Select...",
  className,
  dropDownContainerClass,
  shouldShowError = false,
  disabled = false,
  isSearchable = false,
  searchable = false,
  clearable = false,
  id,
  onMenuScrollToBottom,
  hasMore,
  isLoadingMore,
}) => {
  const enableSearch = Boolean(isSearchable || searchable);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const optionsMapRef = useRef<Map<string, OptionItem>>(new Map());

  const selectedValues = Array.isArray(value) ? value : [];

  // Maintain historical options map so selected item labels are retained when searching remotely
  useEffect(() => {
    options.forEach((opt) => {
      if (opt?.value !== undefined && opt?.value !== null) {
        optionsMapRef.current.set(String(opt.value), opt);
      }
    });
  }, [options]);

  const filteredOptions =
    enableSearch && !onSearch
      ? options.filter((o) =>
          o.label.toLowerCase().includes(searchText.toLowerCase()),
        )
      : options;

  const selectedOptions = selectedValues.map((val) => {
    const foundInCurrent = options.find((o) => String(o.value) === String(val));
    if (foundInCurrent) return foundInCurrent;
    const foundInHistory = optionsMapRef.current.get(String(val));
    if (foundInHistory) return foundInHistory;
    return { label: String(val), value: val };
  });

  const handleSearchChange = (text: string) => {
    setSearchText(text);
    setActiveIndex(0);
    onSearch?.(text);
  };

  const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const target = e.currentTarget;
    if (target.scrollHeight - target.scrollTop - target.clientHeight < 20) {
      if (hasMore && !isLoadingMore && onMenuScrollToBottom) {
        onMenuScrollToBottom();
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        if (searchText !== "") {
          handleSearchChange("");
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchText]);

  useEffect(() => {
    if (isOpen && enableSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, enableSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => {
        const nextIndex = Math.min(
          prev >= 0 ? prev + 1 : 0,
          filteredOptions.length - 1,
        );
        optionRefs.current[nextIndex]?.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
        return nextIndex;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => {
        const nextIndex = Math.max(prev - 1, 0);
        optionRefs.current[nextIndex]?.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
        return nextIndex;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const option = filteredOptions[activeIndex >= 0 ? activeIndex : 0];
      if (option) {
        handleToggleOption(option.value);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      if (searchText !== "") {
        handleSearchChange("");
      }
    }
  };

  const handleToggleOption = (optionValue: string | number) => {
    const isAlreadySelected = selectedValues.some(
      (v) => String(v) === String(optionValue),
    );

    let nextValues: (string | number)[];
    if (isAlreadySelected) {
      nextValues = selectedValues.filter(
        (v) => String(v) !== String(optionValue),
      );
    } else {
      nextValues = [...selectedValues, optionValue];
    }

    onChange?.(nextValues);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.([]);
    if (searchText !== "") {
      handleSearchChange("");
    }
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    const nextOpen = !isOpen;
    if (!isOpen) {
      setActiveIndex(0);
    } else if (searchText !== "") {
      handleSearchChange("");
    }
    setIsOpen(nextOpen);
  };

  const renderTriggerLabel = () => {
    if (selectedOptions.length === 0) {
      return (
        <span className="truncate font-medium text-slate-400 dark:text-slate-500">
          {placeholder}
        </span>
      );
    }

    const labelText = selectedOptions.map((o) => o.label).join(", ");
    return (
      <span className="truncate font-medium text-slate-900 dark:text-slate-100">
        {labelText}
      </span>
    );
  };

  return (
    <div
      ref={wrapperRef}
      id={id}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      className={cn("relative w-full text-left outline-none", className)}
    >
      {/* Trigger Control Box */}
      <div
        onClick={handleTriggerClick}
        className={cn(
          "w-full h-10 px-3.5 flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 text-sm transition-all cursor-pointer select-none",
          shouldShowError &&
            "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500",
          isOpen &&
            !shouldShowError &&
            "ring-2 ring-purple-500 border-purple-500 dark:ring-purple-400",
          disabled &&
            "opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-900",
        )}
      >
        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          {renderTriggerLabel()}
        </div>

        <div className="flex items-center gap-1 shrink-0 ml-2">
          {clearable && selectedOptions.length > 0 && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5 rounded-full transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <ChevronDown
            className={cn(
              "w-4 h-4 text-slate-400 transition-transform duration-200",
              isOpen && "rotate-180 text-purple-600 dark:text-purple-400",
            )}
          />
        </div>
      </div>

      {/* Dropdown Options Container */}
      {isOpen && (
        <div
          className={cn(
            "absolute left-0 right-0 top-[calc(100%+4px)] z-50 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-xl overflow-hidden animate-in fade-in-50 zoom-in-95 duration-100",
            dropDownContainerClass,
          )}
        >
          {enableSearch && (
            <div className="py-3 px-2 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2 bg-slate-50/50 dark:bg-slate-900/50">
              <Search className="w-3.5 h-3.5 text-slate-400 ml-1 shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => handleSearchChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
                className="w-full bg-transparent border-none outline-none text-slate-900 dark:text-slate-100 placeholder:text-slate-400 text-xs font-medium focus:outline-none focus:ring-0"
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => handleSearchChange("")}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          )}

          <ul
            onScroll={handleScroll}
            className="max-h-56 overflow-y-auto p-1 text-sm space-y-0.5"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const isSelected = selectedValues.some(
                  (v) => String(v) === String(option.value),
                );
                const isActive = index === activeIndex;

                return (
                  <li
                    key={option.value}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleOption(option.value);
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      "px-3 py-2 rounded-lg cursor-pointer transition-colors flex items-center text-slate-700 dark:text-slate-300 font-medium group select-none",
                      isSelected &&
                        "bg-purple-50/60 dark:bg-purple-950/30 text-purple-700 dark:text-purple-300 font-semibold",
                      isActive &&
                        !isSelected &&
                        "bg-slate-100 dark:bg-slate-900",
                    )}
                  >
                    {/* Checkbox Icon */}
                    <div
                      className={cn(
                        "w-4 h-4 rounded border transition-all flex items-center justify-center shrink-0 mr-2.5",
                        isSelected
                          ? "bg-purple-600 border-purple-600 text-white"
                          : "border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 group-hover:border-purple-400",
                      )}
                    >
                      {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                    </div>

                    <span className="truncate flex-1">{option.label}</span>
                  </li>
                );
              })
            ) : (
              <li className="px-3 py-4 text-center text-xs text-slate-400 dark:text-slate-500">
                No options found
              </li>
            )}

            {isLoadingMore && (
              <li className="px-3 py-2 text-center text-xs text-purple-600 dark:text-purple-400 font-medium flex items-center justify-center gap-1.5">
                <span className="w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                Loading more...
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const MultiSelectDropdownControlled = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  required,
  labelClassName,
  errorClassName,
  wrapperClassName = "w-full",
  ...rest
}: MultiSelectDropdownProps<TFieldValues>) => {
  const { field, fieldState, formState } = useController({
    control: control!,
    name: name!,
  });

  const shouldShowError =
    !!(fieldState.isTouched || formState.isSubmitted) && !!fieldState.error;

  return (
    <FormFieldShell
      error={shouldShowError ? fieldState.error?.message : undefined}
      htmlFor={name}
      label={label}
      labelClassName={labelClassName}
      errorClassName={errorClassName}
      required={required}
      wrapperClassName={wrapperClassName}
    >
      <MultiSelectDropdownBase
        {...rest}
        id={name}
        value={field.value ?? []}
        onChange={(val) => field.onChange(val)}
        shouldShowError={shouldShowError}
      />
    </FormFieldShell>
  );
};

const MultiSelectDropdownUncontrolled = ({
  label,
  required,
  labelClassName,
  errorClassName,
  wrapperClassName = "w-full",
  value,
  onChange,
  ...rest
}: MultiSelectDropdownProps) => {
  return (
    <FormFieldShell
      htmlFor={rest.placeholder}
      label={label}
      labelClassName={labelClassName}
      errorClassName={errorClassName}
      required={required}
      wrapperClassName={wrapperClassName}
    >
      <MultiSelectDropdownBase {...rest} value={value} onChange={onChange} />
    </FormFieldShell>
  );
};

export const MultiSelectDropdown = <TFieldValues extends FieldValues = any>(
  props: MultiSelectDropdownProps<TFieldValues>,
) => {
  if (props.control && props.name) {
    return <MultiSelectDropdownControlled {...props} />;
  }
  return <MultiSelectDropdownUncontrolled {...props} />;
};

export default MultiSelectDropdown;
